import React from 'react';
import { X, Play, Download, ExternalLink, Calendar, Music, DollarSign } from 'lucide-react';
import { Modal } from '../common/Modal';
import { StatusBadge } from '../common/StatusBadge';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { Song } from '../../types';
import { allStores } from '../../data/mockData';

interface SongDetailsProps {
  song: Song | null;
  isOpen: boolean;
  onClose: () => void;
}

export const SongDetails: React.FC<SongDetailsProps> = ({ song, isOpen, onClose }) => {
  if (!song) return null;

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getStoreInfo = (storeId: string) => {
    return allStores.find(store => store.id === storeId);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" title="Song Details">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start space-x-6">
          <img
            src={song.coverArt}
            alt={song.title}
            className="w-32 h-32 rounded-xl object-cover shadow-lg"
          />
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white font-display mb-2">
              {song.title}
            </h2>
            <p className="text-lg text-gray-300 mb-3">{song.artist}</p>
            <div className="flex items-center space-x-4 mb-4">
              <StatusBadge status={song.status} />
              <span className="text-sm text-gray-400">
                {formatDuration(song.duration)}
              </span>
              <span className="text-sm text-gray-400">
                {song.genre}
              </span>
            </div>
            <div className="flex space-x-3">
              <Button icon={Play} size="sm">
                Play Preview
              </Button>
              <Button variant="outline" icon={Download} size="sm">
                Download
              </Button>
              <Button variant="outline" icon={ExternalLink} size="sm">
                Share
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="text-center p-4">
            <div className="text-2xl font-bold text-gold-400 mb-1">
              {formatNumber(song.streams)}
            </div>
            <div className="text-sm text-gray-400">Total Streams</div>
          </Card>
          <Card className="text-center p-4">
            <div className="text-2xl font-bold text-green-400 mb-1">
              ${song.revenue.toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">Total Revenue</div>
          </Card>
          <Card className="text-center p-4">
            <div className="text-2xl font-bold text-blue-400 mb-1">
              {song.stores.length}
            </div>
            <div className="text-sm text-gray-400">Distribution Stores</div>
          </Card>
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold text-white font-display mb-4">
              Song Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Release Date</span>
                <span className="text-white">
                  {new Date(song.releaseDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Upload Date</span>
                <span className="text-white">
                  {new Date(song.uploadDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Genre</span>
                <span className="text-white">{song.genre}</span>
              </div>
              {song.album && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Album</span>
                  <span className="text-white">{song.album}</span>
                </div>
              )}
              {song.isrc && (
                <div className="flex justify-between">
                  <span className="text-gray-400">ISRC</span>
                  <span className="text-white font-mono text-sm">{song.isrc}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-400">Copyright</span>
                <span className="text-white text-sm">{song.copyright}</span>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-white font-display mb-4">
              Credits
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Artist</span>
                <span className="text-white">{song.artist}</span>
              </div>
              {song.producer && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Producer</span>
                  <span className="text-white">{song.producer}</span>
                </div>
              )}
              {song.featuredArtists && song.featuredArtists.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Featured Artists</span>
                  <span className="text-white">
                    {song.featuredArtists.join(', ')}
                  </span>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Distribution Stores */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Distribution Stores ({song.stores.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {song.stores.map((storeId) => {
              const store = getStoreInfo(storeId);
              if (!store) return null;
              
              return (
                <div
                  key={storeId}
                  className="flex flex-col items-center p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/50 transition-colors"
                >
                  <span className="text-2xl mb-1">{store.logo}</span>
                  <span className="text-xs text-gray-300 text-center">
                    {store.name}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Lyrics */}
        {song.lyrics && (
          <Card>
            <h3 className="text-lg font-semibold text-white font-display mb-4">
              Lyrics
            </h3>
            <div className="bg-gray-800/50 rounded-lg p-4 max-h-60 overflow-y-auto">
              <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">
                {song.lyrics}
              </pre>
            </div>
          </Card>
        )}
      </div>
    </Modal>
  );
};